import { QueryClient, useQuery } from '@tanstack/react-query';

import {
    resourceAssociationService,
    type ResourceAssociationItem,
    type ResourceAssociationsResponse,
} from '@/services';
import { getWorkspaceResourceDetailRoute } from '@/shared/workspaceResourceRoutes';
import { useUserStore, useWorkspaceStore } from '@/stores';
import { CommentMentionInput, ResourceActivityResourceType } from '@/types';

export const resourceAssociationQueryKeys = {
    all: ['resource-associations'] as const,
    detail: (
        workspaceId: string | undefined,
        resourceType: ResourceActivityResourceType,
        resourceId: string | undefined,
    ) => [...resourceAssociationQueryKeys.all, workspaceId ?? 'none', resourceType, resourceId ?? 'none'] as const,
};

interface OptimisticAssociationSource {
    href?: string;
    id: string;
    label: string;
    resourceType: ResourceActivityResourceType;
    website?: string;
}

interface MemoAssociationTarget {
    href?: string;
    id: string;
    label: string;
    mentionCount: number;
    resourceType: ResourceActivityResourceType;
    website?: string;
}

interface AssociationCacheSnapshot {
    previous?: ResourceAssociationsResponse;
    queryKey: ReturnType<typeof resourceAssociationQueryKeys.detail>;
    revision: symbol;
}

const optimisticRevisions = new WeakMap<QueryClient, Map<string, symbol>>();
const memoMentionResourceTypes = new Set<ResourceActivityResourceType>([
    'dashboards',
    'folders',
    'investments',
    'lifecycles',
    'listViews',
    'lists',
    'memos',
    'pipelines',
    'reportComponents',
    'reports',
    'tasks',
    'teams',
]);

const queryKeyIdentity = (queryKey: ReturnType<typeof resourceAssociationQueryKeys.detail>) => JSON.stringify(queryKey);

const optimisticRevisionMap = (queryClient: QueryClient) => {
    const existing = optimisticRevisions.get(queryClient);

    if (existing) {
        return existing;
    }

    const revisions = new Map<string, symbol>();
    optimisticRevisions.set(queryClient, revisions);

    return revisions;
};

const normalizeMemoMentionResourceType = (value: string | null): ResourceActivityResourceType | undefined => {
    if (!value) {
        return undefined;
    }

    const aliases: Record<string, ResourceActivityResourceType> = {
        dashboard: 'dashboards',
        folder: 'folders',
        investment: 'investments',
        lifecycle: 'lifecycles',
        list: 'lists',
        listview: 'listViews',
        listviews: 'listViews',
        memo: 'memos',
        pipeline: 'pipelines',
        report: 'reports',
        reportcomponent: 'reportComponents',
        reportcomponents: 'reportComponents',
        task: 'tasks',
        team: 'teams',
    };
    const normalized = value.trim().toLowerCase();
    const resourceType = aliases[normalized] ?? normalized;

    return memoMentionResourceTypes.has(resourceType as ResourceActivityResourceType)
        ? (resourceType as ResourceActivityResourceType)
        : undefined;
};

/** Extracts and groups the persisted workspace-resource mentions from memo HTML. */
export const memoAssociationTargetsFromHtml = (body?: string): MemoAssociationTarget[] => {
    if (!body || typeof DOMParser === 'undefined') {
        return [];
    }

    const document = new DOMParser().parseFromString(body, 'text/html');
    const targets = new Map<string, MemoAssociationTarget>();

    document.querySelectorAll<HTMLElement>('[data-workspace-mention]').forEach((element) => {
        if (element.getAttribute('kind') !== 'resource') {
            return;
        }

        const id = element.getAttribute('id') ?? element.getAttribute('data-id');
        const resourceType = normalizeMemoMentionResourceType(
            element.getAttribute('resourceType') ??
                element.getAttribute('resourcetype') ??
                element.getAttribute('resource-type') ??
                element.getAttribute('data-resource-type'),
        );

        if (!id || !resourceType) {
            return;
        }

        const key = `${resourceType}:${id}`;
        const existing = targets.get(key);
        const textLabel = element.textContent?.replace(/^@/, '').trim();
        targets.set(key, {
            href: element.getAttribute('href') ?? existing?.href,
            id,
            label: element.getAttribute('label') || existing?.label || textLabel || id,
            mentionCount: (existing?.mentionCount ?? 0) + 1,
            resourceType,
            website: element.getAttribute('website') ?? existing?.website,
        });
    });

    return [...targets.values()];
};

const resourceHref = (resourceType: ResourceActivityResourceType, resourceId: string) => {
    if (resourceType === 'tasks') {
        return `/relations/tasks?taskId=${encodeURIComponent(resourceId)}`;
    }

    return resourceType === 'reports' ? undefined : getWorkspaceResourceDetailRoute(resourceType, resourceId);
};

const withoutMemoContentLocation = (item: ResourceAssociationItem) => ({
    ...item,
    locations: item.locations.filter((location) => location.type !== 'content'),
});

const withMemoContentLocation = (
    item: ResourceAssociationItem | undefined,
    input: MemoAssociationTarget,
): ResourceAssociationItem => ({
    id: item?.id ?? `${input.resourceType}:${input.id}`,
    locations: [
        ...(item?.locations.filter((location) => location.type !== 'content') ?? []),
        {
            id: `optimistic:memo-content:${input.resourceType}:${input.id}`,
            label: 'Memo content',
            mentionCount: input.mentionCount,
            type: 'content',
        },
    ],
    resource: item?.resource ?? {
        href: input.href ?? resourceHref(input.resourceType, input.id),
        id: input.id,
        label: input.label,
        resourceType: input.resourceType,
        website: input.website,
    },
});

const optimisticMemoSourceItems = (currentItems: ResourceAssociationItem[], targets: MemoAssociationTarget[]) => {
    const withoutContent = currentItems.map(withoutMemoContentLocation).filter((item) => item.locations.length > 0);

    return targets.reduce<ResourceAssociationItem[]>((items, target) => {
        const existing = items.find(
            (item) => item.resource.id === target.id && item.resource.resourceType === target.resourceType,
        );
        const next = withMemoContentLocation(existing, target);

        return existing ? items.map((item) => (item === existing ? next : item)) : [...items, next];
    }, withoutContent);
};

const optimisticTargetReferencedBy = (
    currentItems: ResourceAssociationItem[],
    source: OptimisticAssociationSource,
    target?: MemoAssociationTarget,
) => {
    const existing = currentItems.find(
        (item) => item.resource.id === source.id && item.resource.resourceType === source.resourceType,
    );
    const withoutContent = existing ? withoutMemoContentLocation(existing) : undefined;
    const retained = currentItems.filter((item) => item !== existing);

    if (!target) {
        return withoutContent?.locations.length ? [withoutContent, ...retained] : retained;
    }

    return [
        {
            id: existing?.id ?? `${source.resourceType}:${source.id}`,
            locations: [
                ...(withoutContent?.locations ?? []),
                {
                    id: `optimistic:memo-content:${source.id}`,
                    label: 'Memo content',
                    mentionCount: target.mentionCount,
                    type: 'content' as const,
                },
            ],
            resource: existing?.resource ?? {
                href: source.href ?? resourceHref(source.resourceType, source.id),
                id: source.id,
                label: source.label,
                resourceType: source.resourceType,
                website: source.website,
            },
        },
        ...retained,
    ];
};

/**
 * Reconciles both sides of memo-content associations while a memo save is in flight.
 * Per-query revisions prevent an older failed save from rolling back a newer edit.
 */
export const optimisticallyReconcileMemoAssociations = ({
    body,
    previousBody,
    queryClient,
    source,
    workspaceId,
}: {
    body?: string;
    previousBody?: string;
    queryClient: QueryClient;
    source: OptimisticAssociationSource;
    workspaceId?: string;
}) => {
    if (!workspaceId) {
        return { commit: () => undefined, rollback: () => undefined };
    }

    const isSource = (target: MemoAssociationTarget) =>
        target.id === source.id && target.resourceType === source.resourceType;
    const targets = memoAssociationTargetsFromHtml(body).filter((target) => !isSource(target));
    const targetsByKey = new Map(targets.map((target) => [`${target.resourceType}:${target.id}`, target]));
    const affectedTargets = new Map(
        [...memoAssociationTargetsFromHtml(previousBody).filter((target) => !isSource(target)), ...targets].map(
            (target) => [`${target.resourceType}:${target.id}`, target],
        ),
    );
    const revisions = optimisticRevisionMap(queryClient);
    const snapshots: AssociationCacheSnapshot[] = [];

    const updateCache = (
        queryKey: ReturnType<typeof resourceAssociationQueryKeys.detail>,
        update: (current?: ResourceAssociationsResponse) => ResourceAssociationsResponse,
    ) => {
        const revision = Symbol('memo-association-revision');
        const identity = queryKeyIdentity(queryKey);
        snapshots.push({
            previous: queryClient.getQueryData<ResourceAssociationsResponse>(queryKey),
            queryKey,
            revision,
        });
        revisions.set(identity, revision);
        queryClient.setQueryData<ResourceAssociationsResponse>(queryKey, update);
    };

    updateCache(resourceAssociationQueryKeys.detail(workspaceId, source.resourceType, source.id), (current) => {
        const items = optimisticMemoSourceItems(current?.items ?? [], targets);
        const referencedBy = current?.referencedBy ?? [];

        return { items, referencedBy, total: items.length + referencedBy.length };
    });

    affectedTargets.forEach((affectedTarget, key) => {
        updateCache(
            resourceAssociationQueryKeys.detail(workspaceId, affectedTarget.resourceType, affectedTarget.id),
            (current) => {
                const items = current?.items ?? [];
                const referencedBy = optimisticTargetReferencedBy(
                    current?.referencedBy ?? [],
                    source,
                    targetsByKey.get(key),
                );

                return { items, referencedBy, total: items.length + referencedBy.length };
            },
        );
    });

    const forCurrentRevision = (callback: (snapshot: AssociationCacheSnapshot) => void) => {
        snapshots.forEach((snapshot) => {
            const identity = queryKeyIdentity(snapshot.queryKey);

            if (revisions.get(identity) !== snapshot.revision) {
                return;
            }

            revisions.delete(identity);
            callback(snapshot);
        });
    };

    return {
        commit: () =>
            forCurrentRevision(({ queryKey }) => {
                void queryClient.invalidateQueries({ exact: true, queryKey });
            }),
        rollback: () =>
            forCurrentRevision(({ previous, queryKey }) => {
                if (previous) {
                    queryClient.setQueryData(queryKey, previous);
                } else {
                    queryClient.removeQueries({ exact: true, queryKey });
                }
            }),
    };
};

export const optimisticallyAddCommentAssociations = ({
    commentId,
    component,
    mentions,
    queryClient,
    source,
    workspaceId,
}: {
    commentId: string;
    component?: string;
    mentions?: CommentMentionInput[];
    queryClient: QueryClient;
    source: OptimisticAssociationSource;
    workspaceId?: string;
}) => {
    if (!workspaceId) {
        return () => undefined;
    }

    const targets = Array.from(
        new Map(
            (mentions ?? [])
                .filter(
                    (mention): mention is CommentMentionInput & { resourceType: ResourceActivityResourceType } =>
                        mention.kind === 'resource' && Boolean(mention.resourceType),
                )
                .filter((mention) => mention.id !== source.id || mention.resourceType !== source.resourceType)
                .map((mention) => [`${mention.resourceType}:${mention.id}`, mention]),
        ).values(),
    );
    const snapshots = targets.map((target) => {
        const queryKey = resourceAssociationQueryKeys.detail(workspaceId, target.resourceType, target.id);
        const previous = queryClient.getQueryData<ResourceAssociationsResponse>(queryKey);

        queryClient.setQueryData<ResourceAssociationsResponse>(queryKey, (current) => {
            const currentItems = current?.items ?? [];
            const currentReferencedBy = current?.referencedBy ?? [];
            const sourceAssociationId = `${source.resourceType}:${source.id}`;
            const currentAssociation = currentReferencedBy.find((item) => item.id === sourceAssociationId);
            const optimisticLocation = {
                id: commentId,
                label: component ? `Comments · ${component}` : 'Comments',
                mentionCount: 1,
                type: 'comments' as const,
            };

            if (currentAssociation) {
                return {
                    items: currentItems,
                    referencedBy: currentReferencedBy.map((item) =>
                        item.id === sourceAssociationId
                            ? {
                                  ...item,
                                  locations: [
                                      ...item.locations.filter((location) => location.id !== commentId),
                                      optimisticLocation,
                                  ],
                              }
                            : item,
                    ),
                    total: currentItems.length + currentReferencedBy.length,
                };
            }

            return {
                items: currentItems,
                referencedBy: [
                    {
                        id: sourceAssociationId,
                        locations: [optimisticLocation],
                        resource: {
                            href: source.href ?? resourceHref(source.resourceType, source.id),
                            id: source.id,
                            label: source.label,
                            resourceType: source.resourceType,
                            website: source.website,
                        },
                    },
                    ...currentReferencedBy,
                ],
                total: currentItems.length + currentReferencedBy.length + 1,
            };
        });

        return { previous, queryKey };
    });

    return () => {
        snapshots.forEach(({ previous, queryKey }) => {
            if (previous) {
                queryClient.setQueryData(queryKey, previous);
            } else {
                queryClient.removeQueries({ exact: true, queryKey });
            }
        });
    };
};

export const useResourceAssociations = (resourceType: ResourceActivityResourceType, resourceId?: string) => {
    const selectedWorkspaceId = useWorkspaceStore((state) => state.selectedWorkspace?.id);
    const activeWorkspaceId = useUserStore((state) => state.selectedUser?.activeWorkspace?.id);
    const workspaceId = selectedWorkspaceId ?? activeWorkspaceId;

    return useQuery({
        enabled: Boolean(workspaceId && resourceId),
        queryFn: () => resourceAssociationService.list(resourceType, resourceId as string),
        queryKey: resourceAssociationQueryKeys.detail(workspaceId, resourceType, resourceId),
        staleTime: 15_000,
    });
};
